import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { supabase } from "@/integrations/supabase/client";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Calendar, Clock, ArrowLeft } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

interface BlogPost {
  id: number;
  title: string;
  slug: string;
  content_html: string | null;
  meta_description: string | null;
  meta_keywords: string | null;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  infographic_url: string | null;
  faq_schema: Array<{ question: string; answer: string }> | null;
  published_at: string | null;
  updated_at: string | null;
  reading_time: number | null;
  language: string | null;
}

interface RelatedPost {
  id: number;
  title: string;
  slug: string;
  hero_image_url: string | null;
  hero_image_alt: string | null;
  published_at: string | null;
}

const SITE = "https://eap-abutments.com";

const LOCALES: Record<string, string> = {
  de: "de-DE", en: "en-US", fr: "fr-FR", nl: "nl-NL", it: "it-IT", es: "es-ES",
};

const T = {
  de: { back: "Zurück zu allen Artikeln", minRead: "Min. Lesezeit", faq: "Häufig gestellte Fragen", related: "Verwandte Artikel", notFound: "Wir konnten diesen Artikel nicht finden.", backBtn: "← Zurück zu allen Artikeln" },
  en: { back: "Back to all articles", minRead: "min read", faq: "Frequently Asked Questions", related: "Related Articles", notFound: "We couldn't find this article.", backBtn: "← Back to all articles" },
  fr: { back: "Retour à tous les articles", minRead: "min de lecture", faq: "Questions fréquentes", related: "Articles liés", notFound: "Nous n'avons pas trouvé cet article.", backBtn: "← Retour à tous les articles" },
  nl: { back: "Terug naar alle artikelen", minRead: "min leestijd", faq: "Veelgestelde vragen", related: "Gerelateerde artikelen", notFound: "We konden dit artikel niet vinden.", backBtn: "← Terug naar alle artikelen" },
  it: { back: "Torna a tutti gli articoli", minRead: "min di lettura", faq: "Domande frequenti", related: "Articoli correlati", notFound: "Non abbiamo trovato questo articolo.", backBtn: "← Torna a tutti gli articoli" },
  es: { back: "Volver a todos los artículos", minRead: "min de lectura", faq: "Preguntas frecuentes", related: "Artículos relacionados", notFound: "No hemos encontrado este artículo.", backBtn: "← Volver a todos los artículos" },
} as const;

const formatDate = (iso: string | null, lang: string) => {
  if (!iso) return "";
  return new Date(iso).toLocaleDateString(LOCALES[lang] || "en-US", {
    year: "numeric", month: "long", day: "numeric",
  });
};

const BlogPostPage = () => {
  const { slug } = useParams<{ slug: string }>();
  const { language } = useLanguage();
  const t = T[language];
  const [post, setPost] = useState<BlogPost | null>(null);
  const [related, setRelated] = useState<RelatedPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [heroError, setHeroError] = useState(false);
  const [infographicError, setInfographicError] = useState(false);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      setNotFound(false);
      setHeroError(false);
      setInfographicError(false);

      const { data, error } = await supabase
        .from("blog_posts")
        .select("*")
        .eq("slug", slug)
        .maybeSingle();

      if (cancelled) return;

      if (error || !data) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setPost(data as unknown as BlogPost);

      const { data: rel } = await supabase
        .from("blog_posts")
        .select("id, title, slug, hero_image_url, hero_image_alt, published_at")
        .neq("slug", slug)
        .order("published_at", { ascending: false, nullsFirst: false })
        .limit(3);

      if (!cancelled) {
        setRelated((rel ?? []) as RelatedPost[]);
        setLoading(false);
        window.scrollTo({ top: 0, behavior: "instant" as ScrollBehavior });
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="pt-24 pb-16">
          <div className="container mx-auto px-4 max-w-3xl">
            <Skeleton className="h-6 w-32 mb-6" />
            <Skeleton className="h-64 w-full mb-8 rounded-lg" />
            <Skeleton className="h-10 w-3/4 mb-4" />
            <Skeleton className="h-4 w-48 mb-8" />
            <div className="space-y-3">
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-full" />
              <Skeleton className="h-4 w-5/6" />
            </div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (notFound || !post) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <Navigation />
        <main className="flex-1 flex items-center justify-center pt-24 pb-16">
          <div className="text-center px-4">
            <h1 className="text-5xl font-bold text-foreground mb-4">404</h1>
            <p className="text-lg text-muted-foreground mb-6">
              {t.notFound}
            </p>
            <Link to="/blog">
              <Button>{t.backBtn}</Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const description =
    post.meta_description ||
    (post.content_html ? post.content_html.replace(/<[^>]*>/g, " ").slice(0, 160) : "");
  const canonicalUrl = `${SITE}/blog/${post.slug}`;

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    image: post.hero_image_url ? [post.hero_image_url] : undefined,
    datePublished: post.published_at,
    dateModified: post.updated_at || post.published_at,
    description,
    publisher: {
      "@type": "Organization",
      name: "EAP Abutments",
      logo: {
        "@type": "ImageObject",
        url: `${SITE}/images/logo-eap-full.png`,
      },
    },
  };

  const faqSchema =
    post.faq_schema && post.faq_schema.length > 0
      ? {
          "@context": "https://schema.org",
          "@type": "FAQPage",
          mainEntity: post.faq_schema.map((f) => ({
            "@type": "Question",
            name: f.question,
            acceptedAnswer: { "@type": "Answer", text: f.answer },
          })),
        }
      : null;

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{post.title} | EAP Abutments</title>
        <meta name="description" content={description} />
        {post.meta_keywords && <meta name="keywords" content={post.meta_keywords} />}
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={description} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        {post.hero_image_url && <meta property="og:image" content={post.hero_image_url} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={post.title} />
        <meta name="twitter:description" content={description} />
        {post.hero_image_url && <meta name="twitter:image" content={post.hero_image_url} />}
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
        {faqSchema && (
          <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        )}
      </Helmet>

      <Navigation />

      <main className="pt-24 pb-16">
        <article className="container mx-auto px-4 max-w-3xl">
          <Link
            to="/blog"
            className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
          >
            <ArrowLeft className="w-4 h-4" />
            {t.back}
          </Link>

          {post.hero_image_url && !heroError && (
            <div
              className="w-full mb-8 rounded-lg overflow-hidden bg-muted"
              style={{ maxHeight: 400 }}
            >
              <img
                src={post.hero_image_url}
                alt={post.hero_image_alt || post.title}
                className="w-full h-full object-cover"
                style={{ maxHeight: 400 }}
                onError={() => setHeroError(true)}
              />
            </div>
          )}

          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4 leading-tight">
            {post.title}
          </h1>

          <div className="flex flex-wrap items-center gap-4 text-sm text-muted-foreground mb-8 pb-8 border-b border-border">
            {post.published_at && (
              <span className="flex items-center gap-1">
                <Calendar className="w-4 h-4" />
                {formatDate(post.published_at, language)}
              </span>
            )}
            {post.reading_time && (
              <span className="flex items-center gap-1">
                <Clock className="w-4 h-4" />
                {post.reading_time} {t.minRead}
              </span>
            )}
          </div>

          {post.content_html && (
            <div
              className="blog-prose"
              dangerouslySetInnerHTML={{ __html: post.content_html }}
            />
          )}

          {post.infographic_url && !infographicError && (
            <div className="mt-10 rounded-lg overflow-hidden border border-border">
              <img
                src={post.infographic_url}
                alt={`${post.title} infographic`}
                className="w-full h-auto"
                onError={() => setInfographicError(true)}
              />
            </div>
          )}

          {post.faq_schema && post.faq_schema.length > 0 && (
            <section className="mt-12 pt-8 border-t border-border">
              <h2 className="text-2xl font-bold text-foreground mb-6">
                {t.faq}
              </h2>
              <Accordion type="single" collapsible defaultValue="faq-0" className="space-y-2">
                {post.faq_schema.map((faq, idx) => (
                  <AccordionItem
                    key={idx}
                    value={`faq-${idx}`}
                    className="bg-card border border-border rounded-lg px-4"
                  >
                    <AccordionTrigger className="text-left font-semibold hover:no-underline">
                      {faq.question}
                    </AccordionTrigger>
                    <AccordionContent className="text-muted-foreground whitespace-pre-line">
                      {faq.answer}
                    </AccordionContent>
                  </AccordionItem>
                ))}
              </Accordion>
            </section>
          )}

          {related.length > 0 && (
            <section className="mt-16 pt-8 border-t border-border">
              <h2 className="text-2xl font-bold text-foreground mb-6">{t.related}</h2>
              <div className="space-y-4">
                {related.map((r) => (
                  <Link
                    key={r.id}
                    to={`/blog/${r.slug}`}
                    className="flex gap-4 items-center p-3 rounded-lg border border-border hover:border-primary hover:shadow-md transition-all cursor-pointer group"
                  >
                    {r.hero_image_url && (
                      <div className="w-24 h-20 flex-shrink-0 rounded-md overflow-hidden bg-muted">
                        <img
                          src={r.hero_image_url}
                          alt={r.hero_image_alt || r.title}
                          loading="lazy"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            (e.currentTarget.parentElement as HTMLElement).style.display = "none";
                          }}
                        />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-foreground group-hover:text-primary transition-colors line-clamp-2">
                        {r.title}
                      </h3>
                      {r.published_at && (
                        <p className="text-xs text-muted-foreground mt-1">
                          {formatDate(r.published_at, language)}
                        </p>
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          )}
        </article>
      </main>

      <Footer />
    </div>
  );
};

export default BlogPostPage;
